/**
 * Signal capture — the quick-capture surface (SIG-2).
 *
 * The moment it catches (02B §5): "Something just changed — get it on the
 * instruments before it evaporates."
 *
 * Chip + one line is enough; routing class and review date can come later
 * (the friction guard, doc 02 §2B FM2). Chips pre-fill their default
 * routing class; the default is always overridable, and "decide later"
 * leaves the signal unclassified until someone reads it.
 */

import { useState } from 'react';
import {
  HUMAN_SIGNAL_OCCURRENCES,
  SIGNAL_ROUTING_CLASSES,
  defaultRoutingClassFor,
  type SignalRoutingClass,
} from '../../types/signal-events';
import { recordSignalEvent } from '../../lib/signalEventsService';
import { GhostButton, PorModal, PrimaryButton } from '../plan-of-record/porUi';

const ROUTING_LABELS: Record<SignalRoutingClass, string> = {
  feasibility_model: 'Feasibility — what is possible changed',
  value_model: 'Value — what matters changed',
  assumption_base: 'Assumption — something we assumed resolved',
  mandate_frame: 'Mandate — the frame-holders changed',
  record_error: 'Record error — the record was wrong',
};

export interface SignalCapturePrefill {
  /** Pre-selects "Something else…" and fills the free-text occurrence field. */
  freeText?: string;
  message?: string;
  confirmed?: boolean;
  severity?: 'low' | 'medium' | 'high';
}

export function SignalCaptureModal({
  workspaceId,
  porId,
  cycleId,
  planCycleId,
  prefill,
  onClose,
  onRecorded,
}: {
  workspaceId: string;
  porId?: string;
  cycleId?: string;
  /** Planning-family scope (682): attaches the signal to the plan cycle. */
  planCycleId?: string;
  /** Seed the form from an upstream event (e.g. a condition just went broken). */
  prefill?: SignalCapturePrefill;
  onClose: () => void;
  onRecorded: () => Promise<void> | void;
}) {
  const [occurrence, setOccurrence] = useState<string>(prefill?.freeText ? '__free_text' : '');
  const [freeText, setFreeText] = useState(prefill?.freeText ?? '');
  const [message, setMessage] = useState(prefill?.message ?? '');
  const [routing, setRouting] = useState<SignalRoutingClass | ''>('');
  const [confirmed, setConfirmed] = useState(prefill?.confirmed ?? false);
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high'>(prefill?.severity ?? 'medium');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveOccurrence = occurrence === '__free_text' ? freeText.trim() : occurrence;
  const valid = effectiveOccurrence.length > 0;

  const pickChip = (id: string) => {
    setOccurrence(id);
    const suggested = defaultRoutingClassFor(id);
    setRouting(suggested ?? '');
    if (error) setError(null);
  };

  const handleRecord = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    const result = await recordSignalEvent({
      workspaceId,
      occurrence: effectiveOccurrence,
      message: message.trim() || undefined,
      routingClass: routing || undefined,
      status: confirmed ? 'confirmed' : 'monitoring',
      severity,
      porId,
      cycleId,
      planCycleId,
    });
    setBusy(false);
    if (!result.success) {
      setError(result.error ?? 'Could not record the signal.');
      return;
    }
    await onRecorded();
    onClose();
  };

  return (
    <PorModal
      title="Record a signal"
      subtitle="What changed? Anything the plan should know about — good news counts too."
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleRecord} disabled={busy || !valid}>
            {busy ? 'Recording…' : confirmed ? 'Record as confirmed' : 'Record & watch'}
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-wrap gap-1.5 mb-3">
        {HUMAN_SIGNAL_OCCURRENCES.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => pickChip(chip.id)}
            className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
              occurrence === chip.id
                ? 'border-primary-300 bg-primary-50 text-primary-800 dark:bg-[var(--active-tint)] dark:text-[var(--text-pri)]'
                : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            {chip.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => pickChip('__free_text')}
          className={`text-xs rounded-full border border-dashed px-2.5 py-1 transition-colors ${
            occurrence === '__free_text'
              ? 'border-primary-300 bg-primary-50 text-primary-800 dark:bg-[var(--active-tint)] dark:text-[var(--text-pri)]'
              : 'border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--hover-tint)]'
          }`}
        >
          Something else…
        </button>
      </div>

      {occurrence === '__free_text' && (
        <input
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="What happened, in your words"
          className="w-full text-sm border border-[var(--rule)] px-3 py-2 mb-3 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
        />
      )}

      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
        Detail <span className="normal-case font-normal">(optional)</span>
      </label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={2}
        placeholder="One line of context — who saw it, where, how sure."
        className="w-full text-sm border border-[var(--rule)] px-3 py-2 mb-3 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none resize-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
            What it touches
          </label>
          <select
            value={routing}
            onChange={(e) => setRouting(e.target.value as SignalRoutingClass | '')}
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          >
            <option value="">Decide later (classify on reading)</option>
            {SIGNAL_ROUTING_CLASSES.map((rc) => (
              <option key={rc} value={rc}>
                {ROUTING_LABELS[rc]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
            Severity
          </label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as 'low' | 'medium' | 'high')}
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-0.5 rounded border-[var(--rule)]"
        />
        <span>
          This is confirmed — it happened, it is not a rumor.
          <span className="block text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Unchecked records it as watching: seen, not yet confirmed.
          </span>
        </span>
      </label>

      {error && <p className="text-xs text-luminous-coral-700 mt-2">{error}</p>}
    </PorModal>
  );
}
